<template>
  <div class="case-manage">
    <div class="header">
      <h2>案例管理</h2>
      <el-button type="primary" @click="showAddDialog">添加案例</el-button>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
        v-model="searchQuery"
        placeholder="搜索案例标题"
        class="search-input"
        clearable
        @clear="handleSearch"
        @input="handleSearch"
      />
      <el-select v-model="statusFilter" placeholder="状态" @change="handleSearch">
        <el-option label="全部" value="" />
        <el-option label="已发布" value="published" />
        <el-option label="待审核" value="pending" />
      </el-select>
      <el-button type="primary" @click="handleSearch">查询</el-button>
    </div>

    <!-- 案例列表 -->
    <el-table :data="filteredCases" style="width: 100%" v-loading="loading">
      <el-table-column prop="title" label="标题" />
      <el-table-column prop="short_description" label="简介" show-overflow-tooltip />
      <el-table-column prop="tags" label="标签" show-overflow-tooltip />
      <el-table-column label="图片" width="200">
        <template #default="{ row }">
          <div class="image-preview">
            <el-image 
              v-if="row.image_type === 'upload'"
              :src="`/api/uploads/cases/${row.image_path}`"
              :preview-src-list="[`/api/uploads/cases/${row.image_path}`]"
              fit="cover"
              class="preview-image"
            />
            <el-image 
              v-else-if="row.image_url"
              :src="row.image_url"
              :preview-src-list="[row.image_url]"
              fit="cover"
              class="preview-image"
            />
          </div>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
          <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 添加/编辑对话框 -->
    <el-dialog 
      :title="dialogType === 'add' ? '添加案例' : '编辑案例'" 
      v-model="dialogVisible"
      width="60%"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="简介" prop="short_description">
          <el-input v-model="form.short_description" />
        </el-form-item>
        <el-form-item label="详细描述" prop="detail_description">
          <el-input type="textarea" v-model="form.detail_description" rows="4" />
        </el-form-item>
        <el-form-item label="背景" prop="background">
          <el-input type="textarea" v-model="form.background" rows="3" />
        </el-form-item>
        <el-form-item label="技术流程" prop="tech_flow">
          <el-input type="textarea" v-model="form.tech_flow" rows="3" />
        </el-form-item>
        <el-form-item label="反馈" prop="feedback">
          <el-input type="textarea" v-model="form.feedback" rows="3" />
        </el-form-item>
        <el-form-item label="标签" prop="tags">
          <el-input v-model="form.tags" placeholder="用逗号分隔多个标签" />
        </el-form-item>

        <!-- 封面图片 -->
        <el-form-item label="封面图片方式" prop="image_type">
          <el-radio-group v-model="form.image_type">
            <el-radio value="upload">文件上传</el-radio>
            <el-radio value="url">外部链接</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item 
          :label="form.image_type === 'upload' ? '上传封面图片' : '封面图片链接'" 
          :prop="form.image_type === 'upload' ? 'image' : 'image_url'"
        >
          <template v-if="form.image_type === 'upload'">
            <el-upload
              class="upload-demo"
              :action="`/api/admin/content/cases`"
              :headers="uploadHeaders"
              :on-success="handleImageUploadSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeUpload"
              :limit="1"
              :file-list="imageFileList"
              :auto-upload="false"
              :on-change="handleImageChange"
              ref="imageUploadRef"
              name="image"
              accept="image/jpeg,image/png,image/webp"
            >
              <template #trigger>
                <el-button type="primary">选择图片</el-button>
              </template>
              <template #tip>
                <div class="el-upload__tip">
                  支持 jpg/png/webp 格式，大小不超过5MB
                </div>
              </template>
            </el-upload>
          </template>
          <template v-else>
            <el-input v-model="form.image_url" placeholder="输入封面图片URL" />
          </template>
        </el-form-item>

        <!-- 处理前图片 -->
        <el-form-item label="处理前图片方式" prop="before_image_type">
          <el-radio-group v-model="form.before_image_type">
            <el-radio value="upload">文件上传</el-radio>
            <el-radio value="url">外部链接</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item 
          :label="form.before_image_type === 'upload' ? '上传处理前图片' : '处理前图片链接'" 
          :prop="form.before_image_type === 'upload' ? 'before_image' : 'before_image'"
        >
          <template v-if="form.before_image_type === 'upload'">
            <el-upload
              class="upload-demo"
              :action="`/api/admin/content/cases`"
              :headers="uploadHeaders"
              :on-success="handleBeforeImageUploadSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeUpload"
              :limit="1"
              :file-list="beforeImageFileList"
              :auto-upload="false"
              :on-change="handleBeforeImageChange"
              ref="beforeImageUploadRef"
              name="before_image"
              accept="image/jpeg,image/png,image/webp"
            >
              <template #trigger>
                <el-button type="primary">选择图片</el-button>
              </template>
              <template #tip>
                <div class="el-upload__tip">
                  支持 jpg/png/webp 格式，大小不超过5MB
                </div>
              </template>
            </el-upload>
          </template>
          <template v-else>
            <el-input v-model="form.before_image" placeholder="输入处理前图片URL" />
          </template>
        </el-form-item>

        <!-- 处理后图片 -->
        <el-form-item label="处理后图片方式" prop="after_image_type">
          <el-radio-group v-model="form.after_image_type">
            <el-radio value="upload">文件上传</el-radio>
            <el-radio value="url">外部链接</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item 
          :label="form.after_image_type === 'upload' ? '上传处理后图片' : '处理后图片链接'" 
          :prop="form.after_image_type === 'upload' ? 'after_image' : 'after_image'"
        >
          <template v-if="form.after_image_type === 'upload'">
            <el-upload
              class="upload-demo"
              :action="`/api/admin/content/cases`"
              :headers="uploadHeaders"
              :on-success="handleAfterImageUploadSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeUpload"
              :limit="1"
              :file-list="afterImageFileList"
              :auto-upload="false"
              :on-change="handleAfterImageChange"
              ref="afterImageUploadRef"
              name="after_image"
              accept="image/jpeg,image/png,image/webp"
            >
              <template #trigger>
                <el-button type="primary">选择图片</el-button>
              </template>
              <template #tip>
                <div class="el-upload__tip">
                  支持 jpg/png/webp 格式，大小不超过5MB
                </div>
              </template>
            </el-upload>
          </template>
          <template v-else>
            <el-input v-model="form.after_image" placeholder="输入处理后图片URL" />
          </template>
        </el-form-item>

        <!-- 视频 -->
        <el-form-item label="视频方式" prop="video_type">
          <el-radio-group v-model="form.video_type">
            <el-radio value="upload">文件上传</el-radio>
            <el-radio value="url">外部链接</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item 
          :label="form.video_type === 'upload' ? '上传视频' : '视频链接'" 
          :prop="form.video_type === 'upload' ? 'video' : 'video_url'"
        >
          <template v-if="form.video_type === 'upload'">
            <el-upload
              class="upload-demo"
              :action="`/api/admin/content/cases`"
              :headers="uploadHeaders"
              :on-success="handleVideoUploadSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeVideoUpload"
              :limit="1"
              :file-list="videoFileList"
              :auto-upload="false"
              :on-change="handleVideoChange"
              ref="videoUploadRef"
              name="video"
              accept="video/mp4,video/webm"
            >
              <template #trigger>
                <el-button type="primary">选择视频</el-button>
              </template>
              <template #tip>
                <div class="el-upload__tip">
                  支持 mp4/webm 格式，大小不超过50MB
                </div>
              </template>
            </el-upload>
          </template>
          <template v-else>
            <el-input v-model="form.video_url" placeholder="输入视频URL" />
          </template>
        </el-form-item>

        <el-form-item label="发布日期" prop="release_date">
          <el-date-picker
            v-model="form.release_date"
            type="date"
            placeholder="选择发布日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '@/utils/request'

export default {
  name: 'CaseManage',
  setup() {
    const cases = ref([])
    const loading = ref(false)
    const dialogVisible = ref(false)
    const dialogType = ref('add')
    const submitting = ref(false)
    const formRef = ref(null)
    const currentId = ref(null)
    const imageFileList = ref([])
    const beforeImageFileList = ref([])
    const afterImageFileList = ref([])
    const videoFileList = ref([])
    const imageUploadRef = ref(null)
    const beforeImageUploadRef = ref(null)
    const afterImageUploadRef = ref(null)
    const videoUploadRef = ref(null)
    const currentImage = ref(null)
    const currentBeforeImage = ref(null)
    const currentAfterImage = ref(null)
    const currentVideo = ref(null)

    const form = ref({
      title: '',
      short_description: '',
      detail_description: '',
      background: '',
      tech_flow: '',
      feedback: '',
      tags: '',
      image_type: 'upload',
      image_url: '',
      image: null,
      before_image_type: 'upload',
      before_image: null,
      after_image_type: 'upload',
      after_image: null,
      video_type: 'upload',
      video_url: '',
      video: null,
      release_date: null
    })

    const rules = {
      title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
      short_description: [{ required: true, message: '请输入简介', trigger: 'blur' }],
      detail_description: [{ required: true, message: '请输入详细描述', trigger: 'blur' }],
      image_type: [{ required: true, message: '请选择封面图片方式', trigger: 'change' }],
      image: [{ required: true, message: '请上传封面图片', trigger: 'change' }],
      image_url: [{ required: true, message: '请输入封面图片链接', trigger: 'blur' }],
      before_image_type: [{ required: true, message: '请选择处理前图片方式', trigger: 'change' }],
      before_image: [{ required: true, message: '请上传处理前图片或输入URL', trigger: 'blur' }],
      after_image_type: [{ required: true, message: '请选择处理后图片方式', trigger: 'change' }],
      after_image: [{ required: true, message: '请上传处理后图片或输入URL', trigger: 'blur' }],
      video_type: [{ required: true, message: '请选择视频方式', trigger: 'change' }],
      video: [{ required: true, message: '请上传视频', trigger: 'change' }],
      video_url: [{ required: true, message: '请输入视频链接', trigger: 'blur' }]
    }

    // 搜索和筛选
    const searchQuery = ref('')
    const statusFilter = ref('')

    // 过滤后的案例列表
    const filteredCases = computed(() => {
      let result = cases.value

      // 搜索标题
      if (searchQuery.value) {
        result = result.filter(c => 
          c.title.toLowerCase().includes(searchQuery.value.toLowerCase())
        )
      }

      // 筛选状态
      if (statusFilter.value) {
        result = result.filter(c => c.status === statusFilter.value)
      }

      return result
    })

    // 分页相关
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)

    // 获取案例列表
    const fetchCases = async () => {
      loading.value = true
      try {
        const res = await request.get('/admin/content/cases')
        cases.value = res
      } catch (error) {
        console.error('获取案例列表失败:', error)
        ElMessage.error('获取案例列表失败')
      } finally {
        loading.value = false
      }
    }

    // 处理文件上传
    const handleImageChange = (file) => {
      if (file && file.raw) {
        currentImage.value = file.raw
        form.value.image = file.raw
      }
    }

    const handleImageUploadSuccess = (response) => {
      if (response && response.filename) {
        form.value.image = response.filename
        ElMessage.success('封面图片上传成功')
        return true
      } else {
        ElMessage.error('上传响应格式错误')
        return false
      }
    }

    const handleBeforeImageChange = (file) => {
      if (file && file.raw) {
        currentBeforeImage.value = file.raw
        form.value.before_image = file.raw
      }
    }

    const handleAfterImageChange = (file) => {
      if (file && file.raw) {
        currentAfterImage.value = file.raw
        form.value.after_image = file.raw
      }
    }

    const handleVideoChange = (file) => {
      if (file && file.raw) {
        currentVideo.value = file.raw
        form.value.video = file.raw
      }
    }

    const handleBeforeImageUploadSuccess = (response) => {
      if (response && response.filename) {
        form.value.before_image = response.filename
        ElMessage.success('处理前图片上传成功')
        return true
      } else {
        ElMessage.error('上传响应格式错误')
        return false
      }
    }

    const handleAfterImageUploadSuccess = (response) => {
      if (response && response.filename) {
        form.value.after_image = response.filename
        ElMessage.success('处理后图片上传成功')
        return true
      } else {
        ElMessage.error('上传响应格式错误')
        return false
      }
    }

    // 显示添加对话框
    const showAddDialog = () => {
      dialogType.value = 'add'
      form.value = {
        title: '',
        short_description: '',
        detail_description: '',
        background: '',
        tech_flow: '',
        feedback: '',
        tags: '',
        image_type: 'upload',
        image_url: '',
        image: null,
        before_image_type: 'upload',
        before_image: null,
        after_image_type: 'upload',
        after_image: null,
        video_type: 'upload',
        video_url: '',
        video: null,
        release_date: null
      }
      imageFileList.value = []
      beforeImageFileList.value = []
      afterImageFileList.value = []
      videoFileList.value = []
      currentImage.value = null
      currentBeforeImage.value = null
      currentAfterImage.value = null
      currentVideo.value = null
      dialogVisible.value = true
    }

    // 处理编辑
    const handleEdit = (row) => {
      dialogType.value = 'edit'
      currentId.value = row.id
      form.value = {
        title: row.title || '',
        short_description: row.short_description || '',
        detail_description: row.detail_description || '',
        background: row.background || '',
        tech_flow: row.tech_flow || '',
        feedback: row.feedback || '',
        tags: row.tags || '',
        image_type: row.image_type || 'upload',
        image_url: row.image_url || '',
        image: null,
        before_image_type: row.before_image_type || 'upload',
        before_image: row.before_image || '',
        after_image_type: row.after_image_type || 'upload',
        after_image: row.after_image || '',
        video_type: row.video_type || 'upload',
        video_url: row.video_url || '',
        video: null,
        release_date: row.release_date || null
      }
      
      if (row.image_type === 'upload' && row.image_path) {
        imageFileList.value = [{
          name: row.image_path,
          url: `/api/uploads/cases/${row.image_path}`
        }]
      } else {
        imageFileList.value = []
      }

      if (row.before_image_type === 'upload' && row.before_image_path) {
        beforeImageFileList.value = [{
          name: row.before_image_path,
          url: `/api/uploads/cases/${row.before_image_path}`
        }]
      } else {
        beforeImageFileList.value = []
      }

      if (row.after_image_type === 'upload' && row.after_image_path) {
        afterImageFileList.value = [{
          name: row.after_image_path,
          url: `/api/uploads/cases/${row.after_image_path}`
        }]
      } else {
        afterImageFileList.value = []
      }

      if (row.video_type === 'upload' && row.video_path) {
        videoFileList.value = [{
          name: row.video_path,
          url: `/api/uploads/cases/${row.video_path}`
        }]
      } else {
        videoFileList.value = []
      }

      currentImage.value = null
      currentBeforeImage.value = null
      currentAfterImage.value = null
      currentVideo.value = null
      dialogVisible.value = true
    }

    // 处理删除
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确定要删除这个案例吗？', '提示', {
          type: 'warning'
        })
        await request.delete(`/admin/content/cases/${row.id}`)
        ElMessage.success('删除成功')
        fetchCases()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }

    // 提交表单
    const handleSubmit = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        submitting.value = true
        
        const formData = new FormData()
        
        // 添加基本字段
        formData.append('title', form.value.title || '')
        formData.append('short_description', form.value.short_description || '')
        formData.append('detail_description', form.value.detail_description || '')
        formData.append('background', form.value.background || '')
        formData.append('tech_flow', form.value.tech_flow || '')
        formData.append('feedback', form.value.feedback || '')
        formData.append('tags', form.value.tags || '')
        
        // 获取管理员信息
        const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
        if (!adminInfo || !adminInfo.id) {
          throw new Error('未找到管理员信息，请重新登录')
        }
        formData.append('admin_id', adminInfo.id)

        // 处理封面图片
        if (form.value.image_type === 'upload') {
          if (currentImage.value) {
            formData.append('image', currentImage.value)
          }
        } else {
          if (form.value.image_url) {
            formData.append('image_url', form.value.image_url)
          }
        }

        // 处理处理前图片
        if (form.value.before_image_type === 'upload') {
          if (currentBeforeImage.value) {
            formData.append('before_image', currentBeforeImage.value)
          }
        } else {
          if (form.value.before_image) {
            formData.append('before_image', form.value.before_image)
          }
        }

        // 处理处理后图片
        if (form.value.after_image_type === 'upload') {
          if (currentAfterImage.value) {
            formData.append('after_image', currentAfterImage.value)
          }
        } else {
          if (form.value.after_image) {
            formData.append('after_image', form.value.after_image)
          }
        }

        // 处理视频
        if (form.value.video_type === 'upload') {
          if (currentVideo.value) {
            formData.append('video', currentVideo.value)
          }
        } else {
          if (form.value.video_url) {
            formData.append('video_url', form.value.video_url)
          }
        }

        if (form.value.release_date) {
          formData.append('release_date', form.value.release_date)
        }
        
        try {
          if (dialogType.value === 'add') {
            const response = await request.post('/admin/content/cases', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
            ElMessage.success('添加成功')
          } else {
            const response = await request.put(`/admin/content/cases/${currentId.value}`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
            ElMessage.success('更新成功')
          }
          
          dialogVisible.value = false
          fetchCases()
        } catch (submitError) {
          console.error('提交请求错误:', submitError)
          if (submitError.response) {
            console.error('错误响应数据:', submitError.response.data)
            throw new Error(submitError.response.data.message || submitError.response.data.error || '提交失败')
          } else if (submitError.request) {
            throw new Error('服务器无响应，请检查网络连接')
          } else {
            throw new Error(`提交失败: ${submitError.message || '未知错误'}`)
          }
        }
      } catch (error) {
        console.error('表单提交失败:', error)
        ElMessage.error(error.message || (dialogType.value === 'add' ? '添加失败' : '更新失败'))
      } finally {
        submitting.value = false
      }
    }

    // 上传相关
    const uploadHeaders = computed(() => ({
      Authorization: `Bearer ${localStorage.getItem('adminToken')}`
    }))

    const beforeUpload = (file) => {
      const isImage = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isImage) {
        ElMessage.error('只能上传 jpg/png/webp 格式的图片!')
        return false
      }
      if (!isLt5M) {
        ElMessage.error('图片大小不能超过 5MB!')
        return false
      }
      currentImage.value = file
      return false // 阻止自动上传
    }

    const beforeVideoUpload = (file) => {
      const isVideo = ['video/mp4', 'video/webm'].includes(file.type)
      const isLt50M = file.size / 1024 / 1024 < 50

      if (!isVideo) {
        ElMessage.error('只能上传 mp4/webm 格式的视频!')
        return false
      }
      if (!isLt50M) {
        ElMessage.error('视频大小不能超过 50MB!')
        return false
      }
      currentVideo.value = file
      return false // 阻止自动上传
    }

    const handleUploadSuccess = (response) => {
      if (response && response.filename) {
        form.value.image = response.filename
        ElMessage.success('图片上传成功')
        return true
      } else {
        ElMessage.error('上传响应格式错误')
        return false
      }
    }

    const handleVideoUploadSuccess = (response) => {
      if (response && response.filename) {
        form.value.video = response.filename
        ElMessage.success('视频上传成功')
        return true
      } else {
        ElMessage.error('上传响应格式错误')
        return false
      }
    }

    const handleUploadError = (error) => {
      console.error('上传失败:', error)
      ElMessage.error(error.message || '上传失败')
      return false
    }

    // 搜索
    const handleSearch = () => {
      fetchCases()
    }

    // 分页大小改变
    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchCases()
    }

    // 页码改变
    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchCases()
    }

    onMounted(() => {
      fetchCases()
    })

    return {
      cases,
      loading,
      dialogVisible,
      dialogType,
      submitting,
      form,
      formRef,
      rules,
      imageFileList,
      beforeImageFileList,
      afterImageFileList,
      videoFileList,
      uploadHeaders,
      searchQuery,
      statusFilter,
      filteredCases,
      showAddDialog,
      handleEdit,
      handleDelete,
      handleSubmit,
      handleImageUploadSuccess,
      handleBeforeImageUploadSuccess,
      handleAfterImageUploadSuccess,
      handleVideoUploadSuccess,
      handleUploadError,
      beforeUpload,
      beforeVideoUpload,
      handleImageChange,
      handleBeforeImageChange,
      handleAfterImageChange,
      handleVideoChange,
      handleSearch,
      currentPage,
      pageSize,
      total,
      handleSizeChange,
      handleCurrentChange,
      imageUploadRef,
      beforeImageUploadRef,
      afterImageUploadRef,
      videoUploadRef
    }
  }
}
</script>

<style scoped>
.case-manage {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.search-input {
  width: 300px;
}

.image-preview {
  display: flex;
  gap: 10px;
}

.preview-image {
  width: 80px;
  height: 80px;
  border-radius: 4px;
  object-fit: cover;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.el-upload__tip {
  color: #909399;
  font-size: 12px;
  margin-top: 7px;
}
</style>
